import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Send, Video } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import PageTransition, { Section } from "@/components/site/PageTransition";
import Seo from "@/components/site/Seo";
import YouTubeHoverCard from "@/components/site/YouTubeHoverCard";
import Modal from "@/components/site/Modal";

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ artist_name: "", instagram: "", youtube: "", budget: "", location: "", notes: "" });

  useEffect(() => {
    api.get("/videos").then((r) => {
      // Shuffle videos randomly on every page load
      const shuffled = [...r.data].sort(() => Math.random() - 0.5);
      setVideos(shuffled);
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries/video", form);
      toast.success("Inquiry submitted. We'll be in touch.");
      setOpen(false);
      setForm({ artist_name: "", instagram: "", youtube: "", budget: "", location: "", notes: "" });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Submission failed");
    } finally { setSubmitting(false); }
  };

  // group by category
  const grouped = videos.reduce((acc, v) => {
    const k = v.category || "Other";
    acc[k] = acc[k] || [];
    acc[k].push(v);
    return acc;
  }, {});

  return (
    <PageTransition>
      <Seo title="Videos" description="Cinema-grade music videos and visual production — Pulse Media's video catalog." />
      <section className="pt-40 pb-12 px-6 lg:px-10" data-testid="videos-hero">
        <div className="max-w-7xl mx-auto">
          <div className="label-mono mb-4">/ Video Production</div>
          <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tighter leading-[1.0]">
            Cinema-grade. <span className="gradient-text">For every artist.</span>
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl text-lg">
            From epic landscapes to mind-bending CGI, we turn your dreams into reality.
            Solo artist or full band — we tailor the experience to your sound and vision.
          </p>
        </div>
      </section>

      {Object.entries(grouped).map(([cat, vids]) => (
        <Section key={cat} className="px-6 lg:px-10 py-16 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="label-mono mb-2">/ {cat}</div>
                <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tighter">{cat}</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
              {vids.map((v) => (
                <YouTubeHoverCard key={v.id} youtubeId={v.youtube_id} title={v.title} category={v.artist} testid={`video-${v.id}`} />
              ))}
            </div>
          </div>
        </Section>
      ))}

      <Section className="px-6 lg:px-10 py-24 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 mb-5">
            <Video size={16} className="text-cyan-400" /><span className="label-mono">/ Inquiries</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">Ready to make something?</h2>
          <p className="mt-5 text-zinc-400">Tell us about your project. We respond within 48 hours.</p>
          <button onClick={() => setOpen(true)} className="glass-btn solid mt-8" data-testid="videos-inquiry-open">
            Start an inquiry <Send size={14} />
          </button>
        </div>
      </Section>

      <Modal open={open} onClose={() => setOpen(false)} title="Video Production Inquiry" testid="video-inquiry-modal">
        <form onSubmit={submit} className="space-y-4" data-testid="video-inquiry-form">
          <div>
            <label className="label-mono block mb-2">Artist Name *</label>
            <input required className="pulse-input" value={form.artist_name} onChange={(e) => setForm({ ...form, artist_name: e.target.value })} data-testid="vi-artist-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-mono block mb-2">Instagram</label>
              <input className="pulse-input" placeholder="@handle" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} data-testid="vi-instagram" />
            </div>
            <div>
              <label className="label-mono block mb-2">YouTube</label>
              <input className="pulse-input" placeholder="channel/url" value={form.youtube} onChange={(e) => setForm({ ...form, youtube: e.target.value })} data-testid="vi-youtube" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-mono block mb-2">Budget</label>
              <input className="pulse-input" placeholder="$5,000 – $10,000" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} data-testid="vi-budget" />
            </div>
            <div>
              <label className="label-mono block mb-2">Location</label>
              <input className="pulse-input" placeholder="City, State" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="vi-location" />
            </div>
          </div>
          <div>
            <label className="label-mono block mb-2">Project Notes</label>
            <textarea className="pulse-input pulse-textarea" placeholder="Tell us about your concept, treatment, references..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="vi-notes" />
          </div>
          <button type="submit" disabled={submitting} className="glass-btn solid w-full justify-center" data-testid="vi-submit">
            {submitting ? "Sending..." : "Submit Inquiry"} <Send size={14} />
          </button>
        </form>
      </Modal>
    </PageTransition>
  );
}
